
import { AIModule } from '../../types';

export const GAMING_AI_MODULE_2: AIModule = {
  id: 'npc-behavior-ai-decision-making',
  title: 'NPC Behavior & AI Decision Making',
  description: 'Advanced techniques for creating intelligent NPC behavior using decision trees, state machines, and behavior systems.',
  duration: '3.5 hours',
  difficulty: 'medium',
  content: `
# NPC Behavior & AI Decision Making

Creating believable and intelligent Non-Player Characters (NPCs) is crucial for immersive gaming experiences. This module covers advanced AI techniques for NPC behavior and decision-making systems.

## Advanced Behavior Systems

### 1. Hierarchical State Machines
State machines with nested states for complex NPC behaviors.

**Benefits:**
- Organized behavior management
- Easy debugging and modification
- Scalable to complex scenarios
- Clear state transitions

### 2. Goal-Oriented Action Planning (GOAP)
AI system where NPCs have goals and find actions to achieve them.

**Components:**
- **Goals**: What the NPC wants to achieve
- **Actions**: Available behaviors with preconditions and effects
- **World State**: Current situation representation
- **Planner**: Algorithm to find action sequences

## Advanced NPC AI Implementation

\`\`\`python
import heapq
from enum import Enum
from typing import Dict, List, Set, Optional, Tuple
from dataclasses import dataclass

class NPCState(Enum):
    IDLE = "idle"
    PATROL = "patrol"
    INVESTIGATE = "investigate"
    COMBAT = "combat"
    FLEE = "flee"
    INTERACT = "interact"
    WORK = "work"

@dataclass
class WorldState:
    """Represents the current state of the game world from NPC perspective"""
    npc_health: float
    npc_energy: float
    npc_position: Tuple[float, float]
    player_visible: bool
    player_position: Optional[Tuple[float, float]]
    enemies_nearby: int
    allies_nearby: int
    task_assigned: Optional[str]
    inventory_full: bool
    weapon_equipped: bool
    
    def copy(self) -> 'WorldState':
        return WorldState(
            self.npc_health, self.npc_energy, self.npc_position,
            self.player_visible, self.player_position, self.enemies_nearby,
            self.allies_nearby, self.task_assigned, self.inventory_full,
            self.weapon_equipped
        )

@dataclass
class Action:
    """Represents an action an NPC can take"""
    name: str
    cost: float
    preconditions: Dict[str, any]
    effects: Dict[str, any]
    
    def can_execute(self, world_state: WorldState) -> bool:
        """Check if action can be executed given current world state"""
        for condition, value in self.preconditions.items():
            if not hasattr(world_state, condition):
                return False
            if getattr(world_state, condition) != value:
                return False
        return True
    
    def apply_effects(self, world_state: WorldState) -> WorldState:
        """Apply action effects to world state"""
        new_state = world_state.copy()
        for effect, value in self.effects.items():
            if hasattr(new_state, effect):
                setattr(new_state, effect, value)
        return new_state

class GOAPPlanner:
    """Goal-Oriented Action Planning system"""
    
    def __init__(self):
        self.actions: List[Action] = []
        self.setup_actions()
    
    def setup_actions(self):
        """Define available actions for NPCs"""
        self.actions = [
            # Combat actions
            Action(
                name="equip_weapon",
                cost=1.0,
                preconditions={"weapon_equipped": False},
                effects={"weapon_equipped": True}
            ),
            Action(
                name="attack_enemy",
                cost=2.0,
                preconditions={"weapon_equipped": True, "enemies_nearby": 1},
                effects={"enemies_nearby": 0}
            ),
            Action(
                name="flee_from_danger",
                cost=3.0,
                preconditions={"npc_health": 0.3},  # Low health
                effects={"enemies_nearby": 0}
            ),
            
            # Work actions
            Action(
                name="complete_task",
                cost=5.0,
                preconditions={"task_assigned": "gather_resources"},
                effects={"task_assigned": None, "inventory_full": True}
            ),
            Action(
                name="deliver_items",
                cost=2.0,
                preconditions={"inventory_full": True},
                effects={"inventory_full": False}
            ),
            
            # Social actions
            Action(
                name="call_for_help",
                cost=1.0,
                preconditions={"enemies_nearby": 2},  # Outnumbered
                effects={"allies_nearby": 2}
            ),
            Action(
                name="rest_and_recover",
                cost=4.0,
                preconditions={"npc_energy": 0.2},  # Low energy
                effects={"npc_energy": 1.0}
            )
        ]
    
    def plan(self, current_state: WorldState, goal_conditions: Dict[str, any]) -> List[Action]:
        """Find sequence of actions to achieve goal using A* search"""
        
        @dataclass
        class SearchNode:
            state: WorldState
            actions: List[Action]
            cost: float
            
            def __lt__(self, other):
                return self.cost < other.cost
        
        open_set = []
        closed_set = set()
        
        # Start with current state
        start_node = SearchNode(current_state, [], 0.0)
        heapq.heappush(open_set, start_node)
        
        while open_set:
            current_node = heapq.heappop(open_set)
            
            # Check if goal is achieved
            if self.goal_achieved(current_node.state, goal_conditions):
                return current_node.actions
            
            # Add to closed set (simplified state comparison)
            state_key = self.state_to_key(current_node.state)
            if state_key in closed_set:
                continue
            closed_set.add(state_key)
            
            # Try each available action
            for action in self.actions:
                if action.can_execute(current_node.state):
                    new_state = action.apply_effects(current_node.state)
                    new_actions = current_node.actions + [action]
                    new_cost = current_node.cost + action.cost
                    
                    new_node = SearchNode(new_state, new_actions, new_cost)
                    heapq.heappush(open_set, new_node)
        
        return []  # No plan found
    
    def goal_achieved(self, state: WorldState, goal_conditions: Dict[str, any]) -> bool:
        """Check if goal conditions are met"""
        for condition, value in goal_conditions.items():
            if not hasattr(state, condition):
                return False
            if getattr(state, condition) != value:
                return False
        return True
    
    def state_to_key(self, state: WorldState) -> str:
        """Convert state to string key for comparison"""
        return f"{state.npc_health}_{state.weapon_equipped}_{state.enemies_nearby}_{state.task_assigned}"

class AdvancedNPC:
    """Advanced NPC with GOAP-based AI and personality traits"""
    
    def __init__(self, name: str, position: Tuple[float, float]):
        self.name = name
        self.position = position
        self.world_state = WorldState(
            npc_health=1.0,
            npc_energy=1.0,
            npc_position=position,
            player_visible=False,
            player_position=None,
            enemies_nearby=0,
            allies_nearby=0,
            task_assigned=None,
            inventory_full=False,
            weapon_equipped=False
        )
        
        # Personality traits affecting decision making
        self.personality = {
            'aggression': 0.7,    # How likely to engage in combat
            'caution': 0.5,       # How likely to flee or call for help
            'loyalty': 0.8,       # How likely to help allies
            'curiosity': 0.6,     # How likely to investigate
            'persistence': 0.7    # How likely to complete tasks
        }
        
        self.planner = GOAPPlanner()
        self.current_plan: List[Action] = []
        self.current_action_index = 0
        
    def update_world_state(self, **kwargs):
        """Update NPC's perception of the world"""
        for key, value in kwargs.items():
            if hasattr(self.world_state, key):
                setattr(self.world_state, key, value)
    
    def get_current_goal(self) -> Dict[str, any]:
        """Determine current goal based on world state and personality"""
        
        # Emergency situations take priority
        if self.world_state.npc_health < 0.3:
            if self.personality['caution'] > 0.5:
                return {"enemies_nearby": 0}  # Flee goal
        
        if self.world_state.enemies_nearby > 0:
            if self.personality['aggression'] > 0.6:
                return {"enemies_nearby": 0, "weapon_equipped": True}  # Combat goal
            else:
                return {"allies_nearby": 2}  # Call for help goal
        
        # Task-based goals
        if self.world_state.task_assigned:
            return {"task_assigned": None}  # Complete task goal
        
        if self.world_state.inventory_full:
            return {"inventory_full": False}  # Deliver items goal
        
        # Default goal - maintain readiness
        return {"weapon_equipped": True, "npc_energy": 1.0}
    
    def think(self) -> Optional[Action]:
        """Main decision-making process"""
        
        # Check if current plan is still valid
        if not self.current_plan or self.current_action_index >= len(self.current_plan):
            # Need new plan
            goal = self.get_current_goal()
            self.current_plan = self.planner.plan(self.world_state, goal)
            self.current_action_index = 0
            
            if not self.current_plan:
                return None  # No valid plan found
        
        # Execute next action in plan
        if self.current_action_index < len(self.current_plan):
            action = self.current_plan[self.current_action_index]
            
            # Verify action is still valid
            if action.can_execute(self.world_state):
                return action
            else:
                # Plan is invalid, need to replan
                self.current_plan = []
                return self.think()  # Recursive call to get new plan
        
        return None
    
    def execute_action(self, action: Action) -> bool:
        """Execute the given action and update world state"""
        if not action.can_execute(self.world_state):
            return False
        
        # Apply action effects
        self.world_state = action.apply_effects(self.world_state)
        self.current_action_index += 1
        
        print(f"{self.name} executes action: {action.name}")
        return True
    
    def get_status(self) -> Dict:
        """Get current NPC status for debugging"""
        return {
            'name': self.name,
            'position': self.world_state.npc_position,
            'health': self.world_state.npc_health,
            'energy': self.world_state.npc_energy,
            'current_plan': [a.name for a in self.current_plan],
            'plan_progress': f"{self.current_action_index}/{len(self.current_plan)}",
            'personality': self.personality
        }

# Example usage and simulation
def simulate_advanced_npc():
    """Simulate advanced NPC behavior with GOAP"""
    
    # Create NPC
    npc = AdvancedNPC("Guard Captain", (0, 0))
    
    print("=== Advanced NPC Simulation ===")
    print(f"Initial Status: {npc.get_status()}")
    
    # Simulation scenarios
    scenarios = [
        {"enemies_nearby": 1, "description": "Enemy spotted"},
        {"npc_health": 0.2, "description": "NPC takes heavy damage"},
        {"task_assigned": "gather_resources", "description": "New task assigned"},
        {"allies_nearby": 0, "enemies_nearby": 3, "description": "Outnumbered by enemies"}
    ]
    
    for i, scenario in enumerate(scenarios):
        print(f"\\n--- Scenario {i+1}: {scenario['description']} ---")
        
        # Update world state
        scenario_copy = scenario.copy()
        del scenario_copy['description']
        npc.update_world_state(**scenario_copy)
        
        # Let NPC think and act for several steps
        for step in range(3):
            action = npc.think()
            if action:
                success = npc.execute_action(action)
                if success:
                    print(f"Step {step+1}: Successfully executed {action.name}")
                else:
                    print(f"Step {step+1}: Failed to execute {action.name}")
            else:
                print(f"Step {step+1}: No action available")
                break
        
        print(f"Final Status: {npc.get_status()}")

# Run simulation
simulate_advanced_npc()
\`\`\`

## Behavior Trees Implementation

Behavior trees provide a hierarchical approach to AI decision making that's more flexible than state machines.

### Core Node Types

1. **Composite Nodes**
   - **Sequence**: Execute children in order until one fails
   - **Selector**: Execute children until one succeeds
   - **Parallel**: Execute multiple children simultaneously

2. **Decorator Nodes**
   - **Inverter**: Invert child result
   - **Repeater**: Repeat child execution
   - **Cooldown**: Add delay between executions

3. **Leaf Nodes**
   - **Action**: Perform specific behavior
   - **Condition**: Check world state

## Practical Applications

### Combat AI
- Dynamic target selection
- Tactical positioning
- Coordinated group attacks
- Adaptive difficulty scaling

### Social NPCs
- Conversation systems
- Relationship management
- Dynamic quest generation
- Reputation tracking

### Economic NPCs
- Trade behavior
- Resource management
- Market simulation
- Supply chain optimization

Advanced NPC AI creates more engaging and believable game worlds that respond intelligently to player actions and changing game states.
  `,
  quiz: {
    id: 'npc-behavior-quiz',
    title: 'NPC Behavior & AI Decision Making Quiz',
    passThreshold: 80,
    questions: [
      {
        id: 'q1',
        question: 'What is the main advantage of Goal-Oriented Action Planning (GOAP) over simple state machines?',
        type: 'multiple-choice',
        options: [
          'It is faster to execute',
          'It automatically finds action sequences to achieve goals',
          'It uses less memory',
          'It is easier to program'
        ],
        correctAnswer: 1,
        explanation: 'GOAP systems automatically plan sequences of actions to achieve specified goals, making NPC behavior more flexible and adaptive.',
        difficulty: 'medium',
        points: 20
      },
      {
        id: 'q2',
        question: 'In a behavior tree, what does a Selector node do?',
        type: 'multiple-choice',
        options: [
          'Executes all children simultaneously',
          'Executes children in order until one succeeds',
          'Inverts the result of its child',
          'Repeats its child indefinitely'
        ],
        correctAnswer: 1,
        explanation: 'A Selector node tries each child in order and succeeds when the first child succeeds, providing fallback behavior options.',
        difficulty: 'medium',
        points: 15
      }
    ]
  },
  resources: [
    {
      id: 'goap-tutorial',
      title: 'GOAP Implementation Guide',
      type: 'article',
      url: 'https://example.com/goap-guide',
      description: 'Comprehensive guide to implementing Goal-Oriented Action Planning'
    }
  ]
};
